import {
  BuilderDraftSchema, FormDefinitionSchema,
  type BuilderDraft, type BuilderField, type BuilderWarning, type DefinitionResult,
  type FieldProperty, type FormDefinition,
} from "../types/formBuilderTypes";
import { isChoiceWidget } from "./fieldRegistry";

export function createBlankDraft(): BuilderDraft {
  return { fields: {}, elements: [] };
}

function fieldProperty(field: BuilderField): FieldProperty {
  const common = { title: field.label, description: field.helpText };
  const choices = field.choices.map(choice => ({ const: choice.value, title: choice.label }));
  switch (field.widget) {
    case "number": return { ...common, type: "number" };
    case "yesNo": return { ...common, type: "boolean" };
    case "multipleChoice": return {
      ...common, type: "array", uniqueItems: true,
      items: { type: "string", ...(choices.length ? { oneOf: choices } : {}) },
      ...(field.required ? { minItems: 1 } : {}),
    };
    default: return {
      ...common, type: "string",
      ...(field.required ? { minLength: 1 } : {}),
      ...(field.widget === "email" || field.widget === "date" ? { format: field.widget } : {}),
      ...(isChoiceWidget(field.widget) && choices.length ? { oneOf: choices } : {}),
      ...(field.widget === "signature" ? { contentEncoding: "base64", contentMediaType: "image/png" } : {}),
    };
  }
}

export function toFormDefinition(draft: BuilderDraft): FormDefinition {
  return {
    format: "froppio-form",
    formatVersion: 1,
    dataSchema: {
      $schema: "https://json-schema.org/draft/2020-12/schema",
      type: "object",
      properties: Object.fromEntries(Object.values(draft.fields).map(field => [field.id, fieldProperty(field)])),
      required: Object.values(draft.fields).filter(field => field.required).map(field => field.id),
      additionalProperties: false,
    },
    uiSchema: {
      elements: draft.elements.map(element => element.type === "field"
        ? { ...element, widget: draft.fields[element.fieldId].widget }
        : { ...element, elements: element.elements.map(child => ({ ...child, widget: draft.fields[child.fieldId].widget })) }),
    },
  };
}

function sameValue(left: unknown, right: unknown): boolean {
  if (left === right) return true;
  if (Array.isArray(left) || Array.isArray(right)) {
    return Array.isArray(left) && Array.isArray(right)
      && left.length === right.length && left.every((value, index) => sameValue(value, right[index]));
  }
  if (left && right && typeof left === "object" && typeof right === "object") {
    const a = Object.entries(left);
    const b = new Map(Object.entries(right));
    return a.length === b.size && a.every(([key, value]) => b.has(key) && sameValue(value, b.get(key)));
  }
  return false;
}

export function readFormDefinition(text: string): DefinitionResult {
  let input: unknown;
  try {
    input = JSON.parse(text);
  } catch {
    return { success: false, message: "This definition is not valid JSON. Its original text has been preserved." };
  }
  const parsed = FormDefinitionSchema.safeParse(input);
  if (!parsed.success) return { success: false, message: "This definition uses an older or unsupported form format. Its original text has been preserved." };
  const definition = parsed.data;
  const fields: BuilderDraft["fields"] = {};
  const ids = new Set<string>();
  const required = new Set(definition.dataSchema.required);
  const invalid = (): DefinitionResult => ({ success: false, message: "This definition has unsupported or inconsistent fields. Its original text has been preserved." });
  if (required.size !== definition.dataSchema.required.length) return invalid();
  for (const element of definition.uiSchema.elements) {
    if (element.type === "section") {
      if (ids.has(element.id)) return invalid();
      ids.add(element.id);
    }
    for (const child of element.type === "section" ? element.elements : [element]) {
      const property = definition.dataSchema.properties[child.fieldId];
      if (!property || ids.has(child.fieldId)) return invalid();
      ids.add(child.fieldId);
      const field: BuilderField = {
        id: child.fieldId, widget: child.widget, label: property.title,
        helpText: property.description, required: required.has(child.fieldId),
        choices: (property.oneOf ?? property.items?.oneOf ?? []).map(choice => ({ value: choice.const, label: choice.title })),
      };
      if (new Set(field.choices.map(choice => choice.value)).size !== field.choices.length) return invalid();
      if (!sameValue(fieldProperty(field), property)) return invalid();
      fields[field.id] = field;
    }
  }
  if (Object.keys(fields).length !== Object.keys(definition.dataSchema.properties).length
    || [...required].some(id => !fields[id])) return invalid();
  return {
    success: true,
    draft: {
      fields,
      elements: definition.uiSchema.elements.map(element => element.type === "field"
        ? { type: "field", fieldId: element.fieldId }
        : { type: "section", id: element.id, title: element.title, description: element.description,
          elements: element.elements.map(child => ({ type: "field", fieldId: child.fieldId })) }),
    },
  };
}

export function serializeFormDefinition(draft: BuilderDraft) {
  const checked = BuilderDraftSchema.parse(draft);
  const text = JSON.stringify(toFormDefinition(checked));
  const result = readFormDefinition(text);
  if (!result.success) throw new Error(result.message);
  if (!sameValue(checked, result.draft)) throw new Error("This design contains unsupported settings and cannot be saved without losing information.");
  return text;
}

export function getBuilderWarnings(draft: BuilderDraft): BuilderWarning[] {
  const warnings: BuilderWarning[] = [];
  if (!draft.elements.length) warnings.push({ id: "form", message: "Add a question or section to this empty form." });
  for (const element of draft.elements) {
    if (element.type === "section") {
      if (!element.title.trim()) warnings.push({ id: element.id, message: "This section needs a title." });
      if (!element.elements.length) warnings.push({ id: element.id, message: `${element.title || "Untitled section"} has no questions.` });
    }
  }
  for (const field of Object.values(draft.fields)) {
    if (!field.label.trim()) warnings.push({ id: field.id, message: "This question needs a label." });
    if (isChoiceWidget(field.widget)) {
      if (!field.choices.length) warnings.push({ id: field.id, message: `${field.label || "Untitled question"} needs at least one option.` });
      if (field.choices.some(choice => !choice.label.trim())) warnings.push({ id: field.id, message: `${field.label || "Untitled question"} has an option without a label.` });
      const labels = field.choices.map(choice => choice.label.trim().toLocaleLowerCase());
      if (new Set(labels).size !== labels.length) warnings.push({ id: field.id, message: `${field.label || "Untitled question"} has duplicate option labels.` });
    }
  }
  return warnings;
}
